export enum ErrorMessage {
  NotFound = 'Resource could not be found',
  BadRequest = 'Server could not handle your request, please try again',
  FileTooLarge = 'File exceeds the 8 MB limit',
  NotAllowed = 'You are not allowed to do this, please contact support',
  Unauthorized = 'Unauthorized, please login again',
  MaintenanceMode = 'Server is under maintenance',
  CloningNotAllowed = 'Only users belonging to the Nodon organization may clone recipes',
  TemplateNotFound = 'Template project could not be found',
  AppVersionMismatch = 'App client version does not match server version, please refresh the page',
  RaceCondition = 'Simultaneous changes happened to this project, please try again',
  OrganizationRequired = 'Organization is required',
  MissingSharingKey = 'Sharing key is required',
  MissingGenerationKey = 'Generation key is required',
  LocalhostPdfUnreachable = 'PDF export on localhost needs a public tunnel (npm run browserless:tunnel) so cloud Browserless can load the print URL.',
  GeometryImportParseFailed = 'Could not extract a usable building footprint from this file. Try a clearer plan, another page, or add extra instructions.',
  ProjectFileUploadNormalizeFailed = 'Could not flatten the upload. Use a folder of documents or a zip of PDF/images/Office files. Archives with only junk are rejected.',
  ProjectFileUploadTooLarge = 'The upload is too large. A zip may be up to 200 MB. After unpacking, each file may be at most 50 MB and the batch at most 200 files or 200 MB total.',
}

/** Client-facing copy for server {@link ErrorMessage} values that should not be shown verbatim. */
const userFacingErrorMessages: Partial<Record<ErrorMessage, string>> = {
  [ErrorMessage.RaceCondition]:
    'Your changes could not be saved because the project was updated elsewhere. Please try again.',
};

/**
 * Maps a server error string to user-facing copy when defined; otherwise returns the input.
 */
export const toUserFacingErrorMessage = (message: string): string => {
  const messageWithoutTechnicalPrefix = message.replace(
    /^(?:[A-Z][A-Za-z0-9]*ValidationError|Validation error):\s*/,
    '',
  );

  return (
    userFacingErrorMessages[messageWithoutTechnicalPrefix as ErrorMessage] ??
    messageWithoutTechnicalPrefix
  );
};

export const errorStatusCodes: Record<ErrorMessage, number> = {
  [ErrorMessage.NotFound]: 404,
  [ErrorMessage.BadRequest]: 400,
  [ErrorMessage.FileTooLarge]: 413,
  [ErrorMessage.NotAllowed]: 403,
  [ErrorMessage.Unauthorized]: 401,
  [ErrorMessage.MaintenanceMode]: 503,
  [ErrorMessage.CloningNotAllowed]: 403,
  [ErrorMessage.TemplateNotFound]: 404,
  [ErrorMessage.AppVersionMismatch]: 409,
  [ErrorMessage.RaceCondition]: 422,
  [ErrorMessage.OrganizationRequired]: 400,
  [ErrorMessage.MissingSharingKey]: 400,
  [ErrorMessage.MissingGenerationKey]: 400,
  [ErrorMessage.LocalhostPdfUnreachable]: 400,
  [ErrorMessage.GeometryImportParseFailed]: 400,
  [ErrorMessage.ProjectFileUploadNormalizeFailed]: 400,
  [ErrorMessage.ProjectFileUploadTooLarge]: 400,
};
